import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, Loader2, User, X } from 'lucide-react';
import OpenAI from 'openai';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCustomers, useWorkOrders } from '../lib/hooks';

interface AIHelperProps {
  onClose: () => void;
}

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

const AIHelper: React.FC<AIHelperProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant' | 'system', content: string }>>([
    {
      role: 'system',
      content: 'You are a helpful AI assistant for a service business management system. You can help create customers and work orders.'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { addCustomer } = useCustomers();
  const { addWorkOrder } = useWorkOrders();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      // First, analyze the user's intent
      const intentResponse = await openai.chat.completions.create({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'Analyze if this message is about creating a customer or work order. Respond with JSON: {"intent": "customer"|"work_order"|"other", "confidence": 0-1}'
          },
          { role: 'user', content: userMessage }
        ],
        response_format: { type: 'json_object' }
      });

      const intent = JSON.parse(intentResponse.choices[0]?.message?.content || '{}');

      if (intent.intent === 'customer' && intent.confidence > 0.8) {
        // Extract customer information
        const customerResponse = await openai.chat.completions.create({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: 'Extract customer information from this message. Return JSON with: first_name, last_name, email (optional), phone, address, city, state, zip_code'
            },
            { role: 'user', content: userMessage }
          ],
          response_format: { type: 'json_object' }
        });

        const customerData = JSON.parse(customerResponse.choices[0]?.message?.content || '{}');
        
        // Create the customer
        const result = await addCustomer(customerData);
        
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: result.duplicate 
              ? 'I found an existing customer with this phone number. Would you like to create a work order for them instead?'
              : 'I\'ve created a new customer with the following information:\n\n' +
                `Name: ${customerData.first_name} ${customerData.last_name}\n` +
                `Phone: ${customerData.phone}\n` +
                `Address: ${customerData.address}, ${customerData.city}, ${customerData.state} ${customerData.zip_code}`
          }
        ]);
      } else if (intent.intent === 'work_order' && intent.confidence > 0.8) {
        // Extract work order information
        const workOrderResponse = await openai.chat.completions.create({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: 'Extract work order information. Return JSON with: customer_id, service_type, description, priority (low/medium/high/emergency)'
            },
            { role: 'user', content: userMessage }
          ],
          response_format: { type: 'json_object' }
        });

        const workOrderData = JSON.parse(workOrderResponse.choices[0]?.message?.content || '{}');
        
        // Create the work order
        const workOrder = await addWorkOrder(workOrderData);
        
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: 'I\'ve created a new work order:\n\n' +
              `Service Type: ${workOrder.service_type}\n` +
              `Priority: ${workOrder.priority}\n` +
              `Description: ${workOrder.description}`
          }
        ]);
      } else {
        // General conversation
        const response = await openai.chat.completions.create({
          model: 'gpt-3.5-turbo',
          messages: [
            ...messages,
            { role: 'user', content: userMessage }
          ]
        });

        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: response.choices[0]?.message?.content || 'I couldn\'t process that request.'
          }
        ]);
      }
    } catch (err) {
      console.error('Error processing message:', err);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error processing your request. Please try again.'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="flex flex-col h-screen border-l bg-card w-[320px] lg:w-[384px] shrink-0">
      <CardHeader className="border-b space-y-0 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Bot className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">AI Helper</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            title="Hide AI Helper"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Ask me to create customers or work orders
        </p>
      </CardHeader>

      <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.slice(1).map((message, index) => (
          <div
            key={index}
            className={`flex w-full ${
              message.role === 'user' ? 'justify-end' : 'justify-start'
            }`}
          >
            <Card
              className={`max-w-[85%] border-0 ${
                message.role === 'user'
                  ? 'bg-primary text-primary-foreground ml-auto'
                  : 'bg-muted text-foreground mr-auto'
              }`}
            >
              <CardContent className="flex items-start space-x-2 break-words p-3">
                {message.role === 'assistant' ? (
                  <Bot className="w-5 h-5 mt-1" />
                ) : (
                  <User className="w-5 h-5 mt-1 order-last" />
                )}
                <div className="whitespace-pre-wrap break-words">{message.content}</div>
              </CardContent>
            </Card>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </CardContent>

      <CardFooter className="p-4 border-t">
        <form onSubmit={handleSubmit} className="w-full">
        <div className="flex space-x-2">
          <Input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message..."
            disabled={loading}
          />
          <Button
            type="submit"
            disabled={loading || !input.trim()}
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
        </form>
      </CardFooter>
    </Card>
  );
};

export default AIHelper;